import { cn } from "../lib/utils.js";
import { Button } from "./button.js";

type EmptyStateVariant =
  | "default"
  | "library"
  | "paper"
  | "cards"
  | "rewrite"
  | "stats"
  | "templates"
  | "search";

interface EmptyStateProps {
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: "default" | "secondary" | "outline" | "ghost" | "soft" | "destructive";
  };
  className?: string;
  variant?: EmptyStateVariant;
  showVisual?: boolean;
}

const EMPTY_STATE_VISUALS: Record<EmptyStateVariant, string[]> = {
  default: ["+-------+", "|  * *  |", "|   |   |", "+-------+"],
  library: ["   __", "  / /|", " /_/ |", " | | /", " |_|/"],
  paper: ["[PDF]", " / | \\", "[阅读][图表]", "   |", " [AI]"],
  cards: ["[A] [B]", "[C] [D]", " tag  note"],
  rewrite: ["draft  ->  clean", "~~~~      ====", "claim + source"],
  stats: ["mon tue wed", " |   |   |", "_|_  |  _|_", "  reading"],
  templates: ["{guide}", "   +", "[style]", "   =", " output"],
  search: ["search", "  o", " /|\\", "  |"],
};

function inferEmptyStateVariant(title: string): EmptyStateVariant {
  if (title.includes("书")) return "library";
  if (title.includes("论文")) return "paper";
  if (title.includes("卡片")) return "cards";
  if (title.includes("重构")) return "rewrite";
  if (title.includes("统计") || title.includes("阅读记录")) return "stats";
  if (title.includes("模板")) return "templates";
  if (title.includes("没有找到") || title.includes("筛选")) return "search";
  return "default";
}

export function EmptyState({
  title,
  description,
  action,
  className,
  variant,
  showVisual = true,
}: EmptyStateProps) {
  const visualVariant = variant || inferEmptyStateVariant(title);

  return (
    <div
      className={cn(
        "flex h-full flex-col items-center justify-center px-6 py-10 text-center",
        className,
      )}
    >
      {showVisual ? (
        <div className="mb-5 grid h-24 w-32 place-items-center rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-500 shadow-sm dark:border-neutral-800 dark:bg-neutral-900/70 dark:text-neutral-400">
          <pre className="select-none whitespace-pre text-left font-mono text-[11px] leading-4">
            {EMPTY_STATE_VISUALS[visualVariant].join("\n")}
          </pre>
        </div>
      ) : null}
      <h2 className="font-medium font-sans text-base text-neutral-900 dark:text-neutral-100">
        {title}
      </h2>
      {description ? (
        <p className="mt-2 max-w-[360px] text-[13px] text-neutral-500 leading-[1.7] dark:text-neutral-400">
          {description}
        </p>
      ) : null}
      {action ? (
        <Button
          type="button"
          size="sm"
          variant={action.variant || "default"}
          className="mt-5 h-8 px-4 font-medium text-[13px]"
          disabled={action.disabled}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
